/**
 * Logger Factory Presets
 * 
 * Pre-configured factory instances with namespace-specific settings
 * for easy setup and consistent behavior across the application.
 */

import { LogNamespace } from './types';
import {
  UniversalLoggerFactory,
  LoggerFactoryConfig,
  INamespaceLogger,
  NamespaceContext,
  getLoggerFactory
} from './logger-factory';

import {
  NAMESPACE_CONFIGURATIONS,
  NAMESPACE_CONTEXT_PROVIDERS,
  NAMESPACE_DEFAULT_METADATA,
  createNamespaceConfiguration,
  createNamespaceContextProviders,
  createNamespaceDefaultMetadata
} from './namespace-configs';

/**
 * Factory preset configurations for different environments
 */
export const FACTORY_PRESETS = {
  /**
   * Development preset - verbose logging, performance monitoring enabled
   */
  DEVELOPMENT: {
    enableCaching: true,
    cacheTimeout: 180000, // 3 minutes (shorter for development)
    enablePerformanceMonitoring: true,
    enableContextProviders: true,
    defaultConfiguration: {
      enablePerformanceTracking: true,
      enableSensitiveDataFiltering: false, // Relaxed for development
      contextInjection: {
        enabled: true,
        includeUserId: true,
        includeSessionId: true,
        includeRequestId: true,
        includeTimestamp: true,
        customFields: []
      },
      performanceThresholds: {
        operationWarningMs: 500,
        operationErrorMs: 2000,
        memoryWarningMB: 100,
        memoryErrorMB: 500
      }
    }
  },

  /**
   * Production preset - optimized for performance, strict filtering
   */
  PRODUCTION: {
    enableCaching: true,
    cacheTimeout: 600000, // 10 minutes (longer for production)
    enablePerformanceMonitoring: true,
    enableContextProviders: true,
    defaultConfiguration: {
      enablePerformanceTracking: true,
      enableSensitiveDataFiltering: true, // Strict for production
      contextInjection: {
        enabled: true,
        includeUserId: false, // More privacy-conscious
        includeSessionId: false,
        includeRequestId: true,
        includeTimestamp: true,
        customFields: []
      },
      performanceThresholds: {
        operationWarningMs: 100,
        operationErrorMs: 1000,
        memoryWarningMB: 50,
        memoryErrorMB: 200
      }
    }
  },

  /**
   * Testing preset - minimal overhead, no caching
   */
  TESTING: {
    enableCaching: false,
    cacheTimeout: 0,
    enablePerformanceMonitoring: false,
    enableContextProviders: false,
    defaultConfiguration: {
      enablePerformanceTracking: false,
      enableSensitiveDataFiltering: false,
      contextInjection: {
        enabled: false,
        includeUserId: false,
        includeSessionId: false,
        includeRequestId: false,
        includeTimestamp: false,
        customFields: []
      },
      performanceThresholds: {
        operationWarningMs: 10000,
        operationErrorMs: 30000,
        memoryWarningMB: 1000,
        memoryErrorMB: 5000
      }
    }
  },

  /**
   * Security-focused preset - maximum filtering, comprehensive tracking
   */
  SECURITY: {
    enableCaching: true,
    cacheTimeout: 300000, // 5 minutes
    enablePerformanceMonitoring: true,
    enableContextProviders: true,
    defaultConfiguration: {
      enablePerformanceTracking: true,
      enableSensitiveDataFiltering: true,
      contextInjection: {
        enabled: true,
        includeUserId: true,
        includeSessionId: true,
        includeRequestId: true,
        includeTimestamp: true,
        customFields: ['sourceIp', 'userAgent', 'securityLevel']
      },
      performanceThresholds: {
        operationWarningMs: 50,
        operationErrorMs: 500,
        memoryWarningMB: 25,
        memoryErrorMB: 100
      }
    }
  }
} as const;

/**
 * Pre-configured logger factory for specific environments
 */
export class PreConfiguredLoggerFactory {
  private factory: UniversalLoggerFactory;
  private environment: keyof typeof FACTORY_PRESETS;

  constructor(environment: keyof typeof FACTORY_PRESETS = 'DEVELOPMENT') {
    this.environment = environment;
    this.factory = this.createConfiguredFactory();
  }

  /**
   * Create and configure the factory with namespace-specific settings
   */
  private createConfiguredFactory(): UniversalLoggerFactory {
    const baseConfig = FACTORY_PRESETS[this.environment];
    
    // Create namespace configurations map
    const namespaceConfigurations = new Map();
    const namespaceContextProviders = new Map();

    // Configure each namespace
    Object.values(LogNamespace).forEach(namespace => {
      // Get base namespace configuration
      const namespaceConfig = createNamespaceConfiguration(namespace);
      
      // Merge with environment-specific overrides
      const mergedConfig = {
        ...namespaceConfig,
        enablePerformanceTracking: baseConfig.defaultConfiguration.enablePerformanceTracking,
        enableSensitiveDataFiltering: baseConfig.defaultConfiguration.enableSensitiveDataFiltering
      };

      // Store configurations
      namespaceConfigurations.set(namespace, mergedConfig);
      namespaceContextProviders.set(namespace, createNamespaceContextProviders(namespace));
    });

    // Create complete factory configuration
    const factoryConfig: LoggerFactoryConfig = {
      ...baseConfig,
      namespaceConfigurations,
      globalContextProviders: [], // Will be populated by registerGlobalProviders
      namespaceContextProviders,
      // Create mutable copy of default configuration to avoid readonly array issues
      defaultConfiguration: {
        ...baseConfig.defaultConfiguration,
        contextInjection: {
          ...baseConfig.defaultConfiguration.contextInjection,
          customFields: [...baseConfig.defaultConfiguration.contextInjection.customFields]
        }
      }
    };

    // Create factory instance
    const factory = UniversalLoggerFactory.getInstance(factoryConfig);

    // Register global context providers
    this.registerGlobalContextProviders(factory);

    return factory;
  }

  /**
   * Register global context providers that apply to all namespaces
   */
  private registerGlobalContextProviders(factory: UniversalLoggerFactory): void {
    // Environment context provider
    factory.registerGlobalContextProvider({
      name: 'EnvironmentContextProvider',
      priority: 50,
      enabled: true,
      provide: () => ({
        environment: this.environment.toLowerCase(),
        nodeEnv: process.env.NODE_ENV || 'development',
        appVersion: process.env.npm_package_version || '1.0.0'
      })
    });

    // Performance context provider (if enabled)
    if (FACTORY_PRESETS[this.environment].enablePerformanceMonitoring) {
      factory.registerGlobalContextProvider({
        name: 'GlobalPerformanceContextProvider',
        priority: 30,
        enabled: true,
        provide: () => ({
          timestamp: Date.now(),
          performanceNow: performance.now()
        })
      });
    }

    // Request context provider (browser-specific)
    if (typeof window !== 'undefined') {
      factory.registerGlobalContextProvider({
        name: 'BrowserContextProvider',
        priority: 20,
        enabled: true,
        provide: () => ({
          userAgent: navigator.userAgent,
          url: window.location.href,
          referrer: document.referrer || 'direct'
        })
      });
    }

    // Register authentication context providers
    this.registerAuthContextProviders(factory);
  }

  /**
   * Register authentication context providers for automatic user/session injection
   */
  private async registerAuthContextProviders(factory: UniversalLoggerFactory): Promise<void> {
    // User context provider - Provides authenticated user identity and profile information
    factory.registerGlobalContextProvider({
      name: 'UserContextProvider',
      priority: 100,
      enabled: true,
      provide: async () => {
        try {
          // Get Supabase client with performance optimization
          const supabase = await this.getSupabaseClient();
          
          if (!supabase) {
            return {
              isAnonymous: true,
              emailVerified: false
            };
          }

          // Get current user from Supabase
          const { data: { user }, error } = await supabase.auth.getUser();
          
          if (error || !user) {
            return {
              isAnonymous: true,
              emailVerified: false
            };
          }

          // Calculate account age
          const accountAge = this.calculateTimeDifference(new Date(user.created_at), new Date());
          
          // Determine last sign in
          const lastSignIn = user.last_sign_in_at 
            ? this.calculateTimeDifference(new Date(user.last_sign_in_at), new Date())
            : undefined;

          return {
            userId: user.id,
            email: user.email,
            fullName: user.user_metadata?.full_name || undefined,
            username: user.user_metadata?.username || undefined,
            isAnonymous: false,
            accountAge,
            emailVerified: !!user.email_confirmed_at,
            lastSignIn
          };
        } catch (error) {
          console.warn('UserContextProvider error:', error);
          return {
            isAnonymous: true,
            emailVerified: false
          };
        }
      }
    });

    // Session context provider - Provides session state, security level, and timing information
    factory.registerGlobalContextProvider({
      name: 'SessionContextProvider',
      priority: 95,
      enabled: true,
      provide: async () => {
        try {
          // Get Supabase client with performance optimization
          const supabase = await this.getSupabaseClient();
          
          if (!supabase) {
            return {
              hasActiveSession: false,
              securityLevel: 'anonymous',
              isExpired: true,
              refreshable: false
            };
          }

          // Get current session
          const { data: { session }, error } = await supabase.auth.getSession();
          
          if (!session || error) {
            return {
              hasActiveSession: false,
              securityLevel: 'anonymous',
              isExpired: true,
              refreshable: false
            };
          }

          // Calculate session age and expiration
          const now = new Date();
          const expiresAt = new Date(session.expires_at || 0);
          const isExpired = expiresAt.getTime() < now.getTime();
          
          // Estimate session creation time (sessions typically last 24 hours)
          const estimatedCreationTime = new Date(expiresAt.getTime() - (24 * 60 * 60 * 1000));
          const sessionAge = this.calculateTimeDifference(estimatedCreationTime, now);

          // Determine auth method from token metadata
          const authMethod = this.determineAuthMethod(session);
          
          // Determine security level
          const securityLevel = this.determineSecurityLevel(session);

          return {
            sessionId: session.access_token.substring(0, 10) + '...', // Truncated for security
            hasActiveSession: true,
            sessionAge,
            expiresAt: expiresAt.toISOString(),
            authMethod,
            securityLevel,
            isExpired,
            refreshable: !!session.refresh_token
          };
        } catch (error) {
          console.warn('SessionContextProvider error:', error);
          return {
            hasActiveSession: false,
            securityLevel: 'anonymous',
            isExpired: true,
            refreshable: false
          };
        }
      }
    });

    // Device context provider - Provides browser, device, and platform information
    if (typeof window !== 'undefined' && typeof navigator !== 'undefined') {
      factory.registerGlobalContextProvider({
        name: 'DeviceContextProvider',
        priority: 80,
        enabled: true,
        provide: () => {
          try {
            // Parse user agent for device type
            const userAgent = navigator.userAgent;
            const deviceType = this.detectDeviceType(userAgent);

            // Get connection information if available
            const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
            const connectionType = connection?.effectiveType || connection?.type || 'unknown';

            // Get screen information
            const screenResolution = screen.width && screen.height 
              ? `${screen.width}x${screen.height}` 
              : undefined;

            return {
              userAgent,
              platform: navigator.platform,
              deviceType,
              language: navigator.language,
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              screenResolution,
              connectionType
            };
          } catch (error) {
            console.warn('DeviceContextProvider error:', error);
            return {};
          }
        }
      });
    }

    // Request context provider - Provides current page URL and request information
    if (typeof window !== 'undefined') {
      factory.registerGlobalContextProvider({
        name: 'RequestContextProvider',
        priority: 75,
        enabled: true,
        provide: (context) => {
          try {
            const location = window.location;
            const requestContext: any = {
              url: location.href,
              pathname: location.pathname,
              search: location.search,
              referrer: document.referrer || undefined,
              origin: location.origin,
              protocol: location.protocol
            };

            // Add any custom headers from context
            if (context?.customMetadata?.headers) {
              requestContext.headers = context.customMetadata.headers;
            }

            // Add method if available (for API calls)
            if (context?.customMetadata?.method) {
              requestContext.method = context.customMetadata.method;
            }

            return requestContext;
          } catch (error) {
            console.warn('RequestContextProvider error:', error);
            return {};
          }
        }
      });
    }

    // Security context provider - Provides security context and threat assessment
    factory.registerGlobalContextProvider({
      name: 'SecurityContextProvider',
      priority: 90,
      enabled: true,
      provide: async (context) => {
        try {
          // Get Supabase client with performance optimization
          const supabase = await this.getSupabaseClient();
          
          if (!supabase) {
            return {
              threatLevel: 'unknown',
              requiresReauth: false
            };
          }

                     // Get session for security assessment
           const { data: { session }, error } = await supabase.auth.getSession();
           
           if (!session || error) {
             return {
               threatLevel: 'unknown',
               requiresReauth: true
             };
           }
           
           // Determine if operation requires recent authentication
           const sessionAge = session.expires_at ? Date.now() - (session.expires_at * 1000 - 60 * 60 * 1000) : 0;
           const requiresReauth = context?.customMetadata?.sensitiveOperation 
             ? sessionAge > 15 * 60 * 1000 // 15 minutes
             : false;

          // Basic threat assessment (can be enhanced with real security monitoring)
          const threatLevel = this.assessThreatLevel(context);

          return {
            threatLevel,
            requiresReauth,
            sessionSecure: session ? !requiresReauth : false,
            operationType: context?.customMetadata?.operationType || 'standard'
          };
        } catch (error) {
          console.warn('SecurityContextProvider error:', error);
          return {
            threatLevel: 'unknown',
            requiresReauth: false
          };
        }
      }
    });
  }

  // Utility methods for authentication context providers

  /**
   * Calculate human-readable time difference
   */
  private calculateTimeDifference(from: Date, to: Date): string {
    const diffMs = to.getTime() - from.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    const diffMonths = Math.floor(diffDays / 30);

    if (diffMonths > 0) return `${diffMonths} month${diffMonths > 1 ? 's' : ''}`;
    if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? 's' : ''}`;
    if (diffHours > 0) return `${diffHours} hour${diffHours > 1 ? 's' : ''}`;
    if (diffMins > 0) return `${diffMins} minute${diffMins > 1 ? 's' : ''}`;
    return 'just now';
  }

  /**
   * Detect device type from user agent
   */
  private detectDeviceType(userAgent: string): 'mobile' | 'tablet' | 'desktop' | 'unknown' {
    if (/Mobile|Android|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
      return 'mobile';
    }
    if (/iPad|Android(?!.*Mobile)|Tablet/i.test(userAgent)) {
      return 'tablet';
    }
    if (/Windows|Macintosh|Linux/i.test(userAgent)) {
      return 'desktop';
    }
    return 'unknown';
  }

  /**
   * Determine authentication method from session
   */
  private determineAuthMethod(session: any): string {
    // Check for OAuth providers in user metadata
    if (session.user?.app_metadata?.provider) {
      return session.user.app_metadata.provider;
    }
    
    // Default to password-based authentication
    return 'password';
  }

  /**
   * Determine security level from session
   */
  private determineSecurityLevel(session: any): 'anonymous' | 'authenticated' | 'verified' | 'admin' {
    if (!session) return 'anonymous';
    
    // Check for admin role
    if (session.user?.app_metadata?.role === 'admin' || session.user?.user_metadata?.is_admin) {
      return 'admin';
    }
    
    // Check for email verification
    if (session.user?.email_confirmed_at) {
      return 'verified';
    }
    
    return 'authenticated';
  }

  /**
   * Assess threat level based on context
   */
  private assessThreatLevel(context: any): 'low' | 'medium' | 'high' | 'critical' | 'unknown' {
    // Simple threat assessment - can be enhanced with real security monitoring
    let riskFactors = 0;
    
    // Check for high-risk operations
    if (context?.customMetadata?.sensitiveOperation) riskFactors += 2;
    if (context?.customMetadata?.adminOperation) riskFactors += 3;
    if (context?.customMetadata?.failedAttempts > 3) riskFactors += 2;
    if (context?.customMetadata?.suspiciousActivity) riskFactors += 4;
    
    if (riskFactors >= 6) return 'critical';
    if (riskFactors >= 4) return 'high';
    if (riskFactors >= 2) return 'medium';
    if (riskFactors >= 1) return 'low';
    
    return 'low';
  }

  /**
   * Get Supabase client with performance optimization
   * Uses conditional imports to reduce bundle size impact
   */
  private async getSupabaseClient() {
    try {
      // Use conditional dynamic import to avoid static import conflicts
      if (typeof window !== 'undefined') {
        const { supabase } = await import('../../integrations/supabase/client');
        return supabase;
      }
      return null;
    } catch (error) {
      console.warn('Failed to load Supabase client for context providers:', error);
      return null;
    }
  }

  /**
   * Create a namespace-specific logger with enhanced metadata
   */
  createLogger(namespace: LogNamespace, context?: Partial<NamespaceContext>): INamespaceLogger {
    // Enhance context with namespace-specific default metadata
    const defaultMetadata = createNamespaceDefaultMetadata(namespace);
    const enhancedContext: Partial<NamespaceContext> = {
      ...context,
      customMetadata: {
        ...defaultMetadata,
        ...context?.customMetadata
      }
    };

    return this.factory.createLogger(namespace, enhancedContext);
  }

  /**
   * Get the underlying factory instance
   */
  getFactory(): UniversalLoggerFactory {
    return this.factory;
  }

  /**
   * Get current environment
   */
  getEnvironment(): string {
    return this.environment;
  }

  /**
   * Reconfigure for a different environment
   */
  reconfigure(environment: keyof typeof FACTORY_PRESETS): void {
    this.environment = environment;
    UniversalLoggerFactory.resetInstance();
    this.factory = this.createConfiguredFactory();
  }

  /**
   * Get factory statistics with environment context
   */
  getStats() {
    const stats = this.factory.getFactoryStats();
    return {
      ...stats,
      environment: this.environment,
      preset: FACTORY_PRESETS[this.environment]
    };
  }
}

/**
 * Singleton instances for different environments
 */
const factoryInstances = new Map<string, PreConfiguredLoggerFactory>();

/**
 * Get or create a pre-configured factory for an environment
 */
export function getPreConfiguredFactory(
  environment: keyof typeof FACTORY_PRESETS = 'DEVELOPMENT'
): PreConfiguredLoggerFactory {
  if (!factoryInstances.has(environment)) {
    factoryInstances.set(environment, new PreConfiguredLoggerFactory(environment));
  }
  return factoryInstances.get(environment)!;
}

/**
 * Create loggers using environment-aware factory
 */
export const createEnvironmentLogger = {
  /**
   * Create logger based on NODE_ENV
   */
  auto: (namespace: LogNamespace, context?: Partial<NamespaceContext>): INamespaceLogger => {
    const environment = process.env.NODE_ENV === 'production' ? 'PRODUCTION' :
                       process.env.NODE_ENV === 'test' ? 'TESTING' : 'DEVELOPMENT';
    return getPreConfiguredFactory(environment).createLogger(namespace, context);
  },

  /**
   * Create development logger
   */
  development: (namespace: LogNamespace, context?: Partial<NamespaceContext>): INamespaceLogger =>
    getPreConfiguredFactory('DEVELOPMENT').createLogger(namespace, context),

  /**
   * Create production logger
   */
  production: (namespace: LogNamespace, context?: Partial<NamespaceContext>): INamespaceLogger =>
    getPreConfiguredFactory('PRODUCTION').createLogger(namespace, context),

  /**
   * Create testing logger
   */
  testing: (namespace: LogNamespace, context?: Partial<NamespaceContext>): INamespaceLogger =>
    getPreConfiguredFactory('TESTING').createLogger(namespace, context),

  /**
   * Create security logger
   */
  security: (namespace: LogNamespace, context?: Partial<NamespaceContext>): INamespaceLogger =>
    getPreConfiguredFactory('SECURITY').createLogger(namespace, context)
};

/**
 * Namespace-specific logger creators with environment awareness
 */
export const createNamespaceLoggers = {
  auth: (context?: Partial<NamespaceContext>) => 
    createEnvironmentLogger.auto(LogNamespace.AUTH, context),
  
  api: (context?: Partial<NamespaceContext>) => 
    createEnvironmentLogger.auto(LogNamespace.API, context),
  
  ui: (context?: Partial<NamespaceContext>) => 
    createEnvironmentLogger.auto(LogNamespace.UI, context),
  
  performance: (context?: Partial<NamespaceContext>) => 
    createEnvironmentLogger.auto(LogNamespace.PERFORMANCE, context),
  
  database: (context?: Partial<NamespaceContext>) => 
    createEnvironmentLogger.auto(LogNamespace.DATABASE, context),
  
  classification: (context?: Partial<NamespaceContext>) => 
    createEnvironmentLogger.auto(LogNamespace.CLASSIFICATION, context),
  
  judge: (context?: Partial<NamespaceContext>) => 
    createEnvironmentLogger.auto(LogNamespace.JUDGE, context),
  
  storage: (context?: Partial<NamespaceContext>) => 
    createEnvironmentLogger.auto(LogNamespace.STORAGE, context),
  
  worker: (context?: Partial<NamespaceContext>) => 
    createEnvironmentLogger.auto(LogNamespace.WORKER, context),
  
  metrics: (context?: Partial<NamespaceContext>) => 
    createEnvironmentLogger.auto(LogNamespace.METRICS, context),
  
  security: (context?: Partial<NamespaceContext>) => 
    createEnvironmentLogger.auto(LogNamespace.SECURITY, context)
};

/**
 * Reset all factory instances (useful for testing)
 */
export function resetFactoryInstances(): void {
  factoryInstances.clear();
  UniversalLoggerFactory.resetInstance();
}

/**
 * Validate factory preset configuration
 */
export function validateFactoryPreset(presetName: keyof typeof FACTORY_PRESETS): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];
  const preset = FACTORY_PRESETS[presetName];

  if (!preset) {
    errors.push(`Preset '${presetName}' does not exist`);
    return { isValid: false, errors };
  }

  // Validate cache configuration
  if (preset.enableCaching && preset.cacheTimeout <= 0) {
    errors.push('Cache timeout must be positive when caching is enabled');
  }

  // Validate performance thresholds
  const { performanceThresholds } = preset.defaultConfiguration;
  if (performanceThresholds) {
    const { operationWarningMs, operationErrorMs, memoryWarningMB, memoryErrorMB } = performanceThresholds;
    
    if (operationWarningMs && operationErrorMs && operationWarningMs >= operationErrorMs) {
      errors.push('operationWarningMs must be less than operationErrorMs');
    }
    
    if (memoryWarningMB && memoryErrorMB && memoryWarningMB >= memoryErrorMB) {
      errors.push('memoryWarningMB must be less than memoryErrorMB');
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
} 